import { useGetScopeOfWork } from "@components/scope-of-work/hooks/useGetScopeOfWork";
import { KeyboardArrowDown, KeyboardArrowRight, Phone } from "@mui/icons-material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import LinkIcon from "@mui/icons-material/Link";
import NotesIcon from "@mui/icons-material/Notes";
import PaymentIcon from "@mui/icons-material/Payment";
import PendingIcon from "@mui/icons-material/Pending";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ReplayIcon from "@mui/icons-material/Replay";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { useAppSelector } from "src/redux/hooks/redux";
import { getRecordEmail, getRecordPhone } from "src/utils";
import { useModals } from "../../../../../context/ModalContext";
import { BaseModalComponent } from "../../../../common/BaseModalComponent";
import { alphaVar, colors } from "../../../../ui/colors";
import { useDialog } from "../../../../ui/dialogs/useDialog";
import { InvoiceRelatedInvoicesCard } from "../../../create/components/InvoiceRelatedInvoicesCard";
import { useCompanyPaymentsStatusQuery } from "../../../create/hooks/usePayments";
import { EPaymentMethod, IInvoice, IPaymentBankTransfer, IPaymentOnline, TPaymentMethod } from "../../../create/interfaces/invoice.interfaces";
import { useResendCooldown, useResendInvoice, useUpdateInvoice, useUpdateInvoiceRecipients } from "../../hooks/useAdminInvoice";
import { useCancelInvoice } from "../../hooks/useCancelInvoice";
import { useRefundInvoice } from "../../hooks/useRefundInvoice";
import { useSetInvoiceAsPaid } from "../../hooks/useSetBankTransferPaid";
import { formatBillingVisitWindow } from "../../utils/formatBillingVisitWindow";
import { getInvoiceStatus, getInvoiceStatusLabel, type InvoiceStatus } from "../../utils/getInvoiceStatus";
import { getExtraServicesForVisit, getVisitTargetsForExtraItem } from "../../utils/invoiceExtraLineItems";
import { isBalanceFullyCoveredByDeposit } from "../../utils/invoiceNetAfterDeposit";
import { getQuoteGrandTotalFromQuote } from "../../utils/quoteGrandTotal";
import { syncInvoicePayment } from "../ClientView/actions/syncInvoicePayment";
import { useGetInvoice } from "../ClientView/hooks/useGetInvoice";
import { EmailRecipientsEditor } from "./components/EmailRecipientsEditor";
import { InvoiceActionsMenu } from "./components/InvoiceActionsMenu";
import { InvoiceEventsEditor } from "./components/InvoiceEventsEditor";
import { InvoiceSavedCardCharge } from "./components/InvoiceSavedCardCharge";
import { PaymentMethodsEditor } from "./components/PaymentMethodsEditor";
import { useDetailsView } from "./hooks/useDetailsView";
import { useInvoiceDetailsViewSync } from "./hooks/useInvoiceDetailsViewSync";

export interface InvoiceDetailsViewProps {
  invoiceUid: string;
  scopeOfWorkUid?: string | null;
  /** Modal key for the invoice list (e.g. stacked under preview). */
  invoiceListModalKey?: string;
  /** Modal key for this preview window — close this on cancel/refund, not the list. */
  invoicePreviewModalKey?: string;
  /** Visit job-event UID — ensures row / list queries refresh after cancel or refund. */
  jobEventUid?: string;
  onViewDetails?: () => void;
  /** When provided, called after sync-payment so parent can refetch invoice (double-check for Business/Admin). */
  refetchInvoice?: () => void;
}

const statusChipProps = (status: InvoiceStatus) => {
  switch (status) {
    case "UNPAID":
      return {
        bgcolor: colors.warning[100] ?? colors.orange[100],
        color: colors.warning[700] ?? colors.orange[700],
        icon: <PendingIcon sx={{ fontSize: 16 }} />,
      };
    case "PAID":
      return { bgcolor: colors.success[100], color: colors.success[700], icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
    case "REFUNDED":
      return {
        bgcolor: colors.info[100] ?? colors.gray[200],
        color: colors.info[700] ?? colors.gray[700],
        icon: <ReplayIcon sx={{ fontSize: 16 }} />,
      };
    case "CANCELLED":
      return { bgcolor: colors.gray[200], color: colors.gray[700], icon: <CancelIcon sx={{ fontSize: 16 }} /> };
    default:
      return { bgcolor: colors.gray[200], color: colors.gray[700], icon: <PendingIcon sx={{ fontSize: 16 }} /> };
  }
};

export const InvoiceDetailsView = ({
  invoiceUid,
  scopeOfWorkUid,
  refetchInvoice,
  invoiceListModalKey,
  invoicePreviewModalKey,
  jobEventUid,
}: InvoiceDetailsViewProps) => {
  const { showDialog, DialogComponent } = useDialog();
  const { openModal } = useModals();
  const { data: invoice, isLoading, isFetching } = useGetInvoice(invoiceUid);
  const company = useAppSelector((state) => state.userState?.company);
  const { data: paymentAvailableData, isLoading: paymentLoading } = useCompanyPaymentsStatusQuery(company?.id);
  const invoiceStatus = getInvoiceStatus(invoice);
  const isPaid = !!invoice?.paidAt && !!invoice?.paidWithPaymentMethod;
  const isRefunded = !!invoice?.refundedAt;
  const currency = invoice?.totalCost?.total?.currency || "USD";
  const refundAmount = invoice?.totalCost?.total?.value ?? 0;
  const canRefund = isPaid && !isRefunded && invoice?.paidWithPaymentMethod === EPaymentMethod.PAYPAL;
  const canCancel = invoiceStatus === "UNPAID";
  const effectiveScopeOfWorkUid = scopeOfWorkUid ?? invoice?.scopeOfWorkUid ?? invoice?.jobEvent?.job?.scopeOfWorkUid;

  const { data: scopeOfWork } = useGetScopeOfWork({
    scopeOfWorkUid: effectiveScopeOfWorkUid,
  });
  const { showSnackbar } = useGlobalSnackbar();

  const canMarkAsPaid = !isPaid && !isRefunded && invoiceStatus !== "CANCELLED";

  const { refundInvoice, isPending: isRefundPending } = useRefundInvoice();
  const { cancelInvoice, isPending: isCancelPending } = useCancelInvoice();
  const { resendInvoice, isPending: isResendPending } = useResendInvoice();
  const { remainingSec: resendRemainingSec, isCoolingDown: isResendCoolingDown } = useResendCooldown((invoice as any)?.lastResendAt);
  const { updateInvoice, isPending: isUpdatingInvoice } = useUpdateInvoice();
  const { updateRecipients, isPending: isUpdatingRecipients } = useUpdateInvoiceRecipients();
  const [isEditingTotal, setIsEditingTotal] = useState(false);
  const [totalDraft, setTotalDraft] = useState<string>("");
  const [isSyncingPayment, setIsSyncingPayment] = useState(false);

  const isEditable = invoiceStatus === "UNPAID";

  // Deposit invoices are linked to a Quote and their total is capped by the quote grand total.
  const quote: any = (invoice as any)?.quote ?? null;
  const isDepositInvoice = !!quote && (invoice?.billingJobEvents?.length ?? 0) === 0;

  /** Same deposit link as Create Invoice: parent deposit on final invoices, or quote deposit when included. */
  const relatedDepositInvoiceUid = useMemo(() => {
    const parent = invoice?.parentDepositInvoiceUid;
    if (parent) return parent;
    const qDep = quote?.depositInvoiceUid != null ? String(quote.depositInvoiceUid) : null;
    if (!isDepositInvoice && qDep && invoice?.uid && qDep !== invoice.uid) return qDep;
    return null;
  }, [invoice?.parentDepositInvoiceUid, invoice?.uid, quote?.depositInvoiceUid, isDepositInvoice]);

  const relatedDepositAmountLabel =
    quote && relatedDepositInvoiceUid && String(quote.depositInvoiceUid ?? "") === relatedDepositInvoiceUid ? quote.depositAmount : undefined;
  const quoteGrandTotal: number = getQuoteGrandTotalFromQuote(quote);
  const totalDraftValue = Number(totalDraft);
  const exceedsQuoteTotal = isDepositInvoice && quoteGrandTotal > 0 && Number.isFinite(totalDraftValue) && totalDraftValue > quoteGrandTotal;

  const hasPayPalPending = !isPaid && !!invoice?.paymentMethods?.some((pm: any) => pm.provider === EPaymentMethod.PAYPAL && pm.externalOrderUid);

  const {
    invalidateRelatedQueries,
    quoteId: quoteIdForMutations,
    billingJobEventUids,
  } = useInvoiceDetailsViewSync({
    invoiceUid,
    invoice,
    refetchInvoice,
    hasPayPalPending,
  });

  // handleMarkAsPaidClick is now handled inside MarkAsPaidModalContent

  const handleVerifyPaymentStatus = async () => {
    if (!invoice?.uid || isSyncingPayment) return;
    setIsSyncingPayment(true);
    try {
      const result = await syncInvoicePayment(invoice?.uid);
      if (result?.synced) {
        invalidateRelatedQueries();
        refetchInvoice?.();
      }
    } finally {
      setIsSyncingPayment(false);
    }
  };

  const handleRefundClick = () => {
    showDialog({
      type: "confirm",
      title: "Confirm Refund",
      message: `Are you sure you want to refund ${refundAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency} to the buyer? This action cannot be undone.`,
      confirmLabel: "Confirm Refund",
      cancelLabel: "Cancel",
      onConfirm: () =>
        refundInvoice(invoice?.uid, {
          billingJobEventUids: (invoice?.billingJobEvents ?? []).map((e) => e.uid),
          extraInvalidateJobEventUids: jobEventUid ? [jobEventUid] : undefined,
          quoteId: quoteIdForMutations,
          invoiceModal: invoicePreviewModalKey ?? invoiceListModalKey,
        }),
      onCancel: () => {},
    });
  };

  const handleCancelClick = () => {
    const identifier = invoice?.identifierId ?? invoice?.uid?.slice(0, 8) ?? "invoice";
    const amount = invoice?.totalCost?.total?.value ?? 0;
    showDialog({
      type: "confirm",
      title: "Cancel Invoice",
      message: `Are you sure you want to cancel invoice ${identifier} for ${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}? The buyer will no longer be able to pay this invoice?. This action cannot be undone.`,
      confirmLabel: "Cancel Invoice",
      cancelLabel: "Keep Invoice",
      onConfirm: () => {
        cancelInvoice(invoice?.uid, {
          billingJobEventUids: (invoice?.billingJobEvents ?? []).map((e) => e.uid),
          extraInvalidateJobEventUids: jobEventUid ? [jobEventUid] : undefined,
          quoteId: quoteIdForMutations,
          invoiceModal: invoicePreviewModalKey ?? invoiceListModalKey,
        });
      },
      onCancel: () => {},
    });
  };

  const handleResendClick = () => {
    if (!invoice?.uid) return;
    resendInvoice({ invoiceUid: invoice.uid, jobEventUids: billingJobEventUids, quoteId: quoteIdForMutations });
  };

  const startEditingTotal = () => {
    setTotalDraft(String(invoice?.totalCost?.total?.value ?? 0));
    setIsEditingTotal(true);
  };

  const cancelEditingTotal = () => {
    setIsEditingTotal(false);
    setTotalDraft("");
  };

  const saveTotal = () => {
    if (!invoice?.uid) return;
    const nextValue = Number(totalDraft);
    if (!Number.isFinite(nextValue) || nextValue < 0) return;
    if (isDepositInvoice && quoteGrandTotal > 0 && nextValue > quoteGrandTotal) return;
    const previousValue = invoice?.totalCost?.total?.value ?? 0;
    if (previousValue === nextValue) {
      setIsEditingTotal(false);
      return;
    }
    const nextTotalCost = {
      ...(invoice.totalCost ?? {}),
      total: {
        ...((invoice.totalCost as any)?.total ?? {}),
        value: nextValue,
        currency: (invoice.totalCost as any)?.total?.currency ?? currency,
      },
    };
    const fmt = (v: number) => `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
    showDialog({
      type: "confirm",
      title: "Update Invoice Total",
      message: `This will change the total from ${fmt(previousValue)} to ${fmt(nextValue)} and send an update notification to all recipients with the new payment amount. Continue?`,
      confirmLabel: "Update & Notify",
      cancelLabel: "Cancel",
      onConfirm: () => {
        updateInvoice(
          {
            invoiceUid: invoice.uid,
            patch: { totalCost: nextTotalCost } as Partial<IInvoice>,
            jobEventUids: billingJobEventUids,
            quoteId: quoteIdForMutations,
          },
          { onSuccess: () => setIsEditingTotal(false) },
        );
      },
      onCancel: () => {},
    });
  };

  const handleSaveRecipients = (next: IInvoice["emailRecipients"]) => {
    if (!invoice?.uid) return;
    updateRecipients({ invoiceUid: invoice.uid, emailRecipients: next, jobEventUids: billingJobEventUids, quoteId: quoteIdForMutations });
  };

  const handleSavePaymentMethods = (nextMethods: TPaymentMethod[]) => {
    if (!invoice?.uid) return;
    updateInvoice({
      invoiceUid: invoice.uid,
      patch: { paymentMethods: nextMethods } as Partial<IInvoice>,
      jobEventUids: billingJobEventUids,
      quoteId: quoteIdForMutations,
    });
  };

  const handleSaveJobEvents = (patchPayload: { jobEventsPayload: any }) => {
    if (!invoice?.uid) return;
    updateInvoice({
      invoiceUid: invoice.uid,
      patch: patchPayload as any,
      jobEventUids: billingJobEventUids,
      quoteId: quoteIdForMutations,
    });
  };

  const {
    mainAreas,
    otherAreas,
    additionalObjects,
    specialRequests,
    invoiceCosts,
    toggleRow,
    isRowExpanded,
    hasMainAreasData,
    hasOtherAreasData,
    hasAdditionalObjectsData,
    hasSpecialRequestsData,
  } = useDetailsView({
    scopeOfWorkUid: effectiveScopeOfWorkUid,
    invoice,
  });

  // Format payment method
  const getPaymentMethodLabel = (method: EPaymentMethod | null | undefined): string => {
    if (!method) return "Not specified";
    switch (method) {
      case EPaymentMethod.PAYPAL:
        return "PayPal";
      case EPaymentMethod.STRIPE:
        return "Stripe";
      case EPaymentMethod.BANK_TRANSFER:
        return "Bank Transfer";
      case EPaymentMethod.MANUAL_ENTRY:
        return "Manual Entry";
      default:
        return method;
    }
  };

  // Format date
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format date time
  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get invoice number from identifierId or UID
  const invoiceNumber = invoice?.identifierId || `INV-${invoice?.uid.substring(0, 8).toUpperCase()}`;
  const billedEvents = invoice?.billingJobEvents ?? (invoice?.jobEvent ? [invoice.jobEvent] : []);
  const billedEventsByJob = billedEvents.reduce((acc, ev) => {
    const key = Number(ev?.job?.id ?? ev?.jobId ?? 0);
    if (!key) return acc;
    if (!acc.has(key)) acc.set(key, []);
    acc.get(key)!.push(ev);
    return acc;
  }, new Map<number, any[]>());
  const billedJobEntries = Array.from(billedEventsByJob.entries()).sort((a, b) => a[0] - b[0]);
  const serviceSubtotalValue = Number(invoice?.mainAreasCost?.total?.value ?? 0);

  const serviceLineAmountByJob = useMemo(() => {
    const m = new Map<number, number>();
    if (billedJobEntries.length === 0 || serviceSubtotalValue <= 0) return m;

    const savedLines = (invoice as any)?.pricingDetail?.serviceLines;
    if (Array.isArray(savedLines)) {
      for (const line of savedLines) {
        if (line && typeof line.jobId === "number" && typeof line.lineTotal === "number") {
          m.set(line.jobId, (m.get(line.jobId) ?? 0) + line.lineTotal);
        }
      }
      if (billedJobEntries.every(([jobId]) => m.has(jobId))) {
        return m;
      }
    }

    const perJobFallback = serviceSubtotalValue / billedJobEntries.length;
    for (const [jobId] of billedJobEntries) {
      m.set(jobId, perJobFallback);
    }
    return m;
  }, [billedJobEntries, serviceSubtotalValue, invoice?.pricingDetail]);

  // Get client info from job events or quote (for deposit invoices)
  const client = invoice?.jobEvent?.job?.client || invoice?.quote?.client;
  const clientName = client ? `${client?.firstName} ${client?.lastName}` : "N/A";
  const clientEmail = getRecordEmail(client);
  const clientPhone = getRecordPhone(client);

  // Helper to format monetary amount
  const formatAmount = (record: any): string => {
    if (!record?.total?.value) return "$0.00";
    return `$${record.total.value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatUsdAmount = (amount: number, negative = false): string => {
    const formatted = `$${Math.abs(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return negative ? `-${formatted}` : formatted;
  };

  const formatAppliesToNote = (appliesTo?: { label?: string | null; type?: string | null } | null): string | undefined => {
    const label = String(appliesTo?.label ?? "").trim();
    if (label) return `Applies to ${label}`;
    if (appliesTo?.type === "service") return "Applies to a specific service";
    if (appliesTo?.type === "extra_line_item") return "Applies to an extra line item";
    if (appliesTo?.type === "invoice") return "Applies to entire invoice";
    return undefined;
  };

  const formatAdjustmentKind = (kind?: string, value?: number): string | undefined => {
    if (kind === "percentage" && Number.isFinite(Number(value))) return `${Number(value)}%`;
    if (kind === "amount") return "Fixed amount";
    return undefined;
  };

  // Check if payment method is online
  const isOnlinePayment = (pm: any): pm is IPaymentOnline => {
    return pm?.provider === EPaymentMethod.PAYPAL || pm?.provider === EPaymentMethod.STRIPE;
  };

  // Check if payment method is bank transfer
  const isBankTransfer = (pm: any): pm is IPaymentBankTransfer => {
    return pm?.provider === EPaymentMethod.BANK_TRANSFER;
  };

  const cardStyles = {
    p: 3,
    borderRadius: 2,
    border: `1px solid ${alphaVar(colors.gray[200], 0.5)}`,
    bgcolor: "background.paper",
    boxShadow: "none",
  };

  if (isLoading || isFetching) return <div>Loading...</div>;
  const pricingDetailAny = (invoice as any)?.pricingDetail ?? null;
  const persistedServiceFeeLabel =
    typeof pricingDetailAny?.serviceFeeLabel === "string" && pricingDetailAny.serviceFeeLabel.trim().length > 0
      ? pricingDetailAny.serviceFeeLabel
      : null;
  const persistedExtraLineItems: Array<{
    id?: string | null;
    title: string;
    amount: number;
    unitPrice?: number | null;
    allocations?: Array<{ eventUid: string; amount: number; unitPrice?: number | null }>;
    appliesTo?: any;
    description?: string;
  }> = Array.isArray(pricingDetailAny?.extraLineItems)
    ? pricingDetailAny.extraLineItems
        .map((item: any) => ({
          id: item?.id ?? null,
          title: String(item?.title ?? "").trim(),
          amount: Number(item?.amount ?? 0),
          unitPrice: item?.unitPrice != null ? Number(item.unitPrice) : undefined,
          allocations: Array.isArray(item?.allocations) ? item.allocations : undefined,
          appliesTo: item?.appliesTo,
          description: String(item?.description ?? "").trim() || undefined,
        }))
        .filter((item) => item.title.length > 0 && Number.isFinite(item.amount) && item.amount > 0)
    : [];
  const persistedExtraLineItemsTotal = persistedExtraLineItems.reduce((acc, item) => acc + item.amount, 0);
  const persistedServiceFees: Array<{ label: string; amount: number; description?: string }> = Array.isArray(pricingDetailAny?.serviceFees)
    ? pricingDetailAny.serviceFees
        .map((item: any) => {
          const amount = Number(item?.total?.value ?? item?.amount ?? 0);
          const label = String(item?.alias ?? persistedServiceFeeLabel ?? "Service fee").trim() || "Service fee";
          const kindNote = formatAdjustmentKind(item?.kind, item?.value);
          const appliesToNote = formatAppliesToNote(item?.appliesTo);
          const description = [kindNote, appliesToNote].filter(Boolean).join(" · ") || undefined;
          return { label, amount, description };
        })
        .filter((item: { label: string; amount: number }) => item.label.length > 0 && Number.isFinite(item.amount) && item.amount > 0)
    : [];
  const persistedServiceFeesTotal = persistedServiceFees.reduce((acc, item) => acc + item.amount, 0);
  const persistedDiscounts: Array<{ description: string; amount: number; detail?: string }> = Array.isArray(pricingDetailAny?.discounts)
    ? pricingDetailAny.discounts
        .map((item: any) => {
          const amount = Number(item?.amount ?? 0);
          const description = String(item?.description ?? "").trim();
          const kindNote = formatAdjustmentKind(item?.kind, item?.value);
          const appliesToNote = formatAppliesToNote(item?.appliesTo);
          const detail = [kindNote, appliesToNote].filter(Boolean).join(" · ") || undefined;
          return { description, amount, detail };
        })
        .filter((item: { description: string; amount: number }) => item.description.length > 0 && Number.isFinite(item.amount) && item.amount > 0)
    : [];
  const persistedDiscountsTotal = persistedDiscounts.reduce((acc, item) => acc + item.amount, 0);
  const persistedTipAmount = Number(invoice?.tipAmount ?? pricingDetailAny?.tip?.amount ?? 0);
  const persistedTipDetail = pricingDetailAny?.tip
    ? pricingDetailAny.tip.kind === "percentage"
      ? `${pricingDetailAny.tip.value}% of invoice total`
      : "Fixed amount"
    : null;
  const completedPayment = invoice?.payments
    ?.filter((payment) => payment.status === "completed")
    ?.sort((a, b) => new Date(b.paidAt ?? b.createdAt ?? 0).getTime() - new Date(a.paidAt ?? a.createdAt ?? 0).getTime())?.[0];

  const paymentMethodLabel = completedPayment?.paymentMethod ? getPaymentMethodLabel(completedPayment.paymentMethod as EPaymentMethod) : null;

  const paymentNote = completedPayment?.gatewayReference?.trim() || completedPayment?.transactionId?.trim() || null;
  const paymentMethodChipStyles = {
    bgcolor: alphaVar(colors.success[100], 0.65),
    color: colors.success[700],
    iconColor: colors.success[600],
  };

  return (
    <Box sx={{ p: 3, bgcolor: colors.gray[50], minHeight: "100%" }}>
      {/* <BaseModalComponent
        key={`quote-${quote?.id}`}
        modalKey={`QuoteRow-ViewQuote-${quote?.id}`}
        modalLabel="Quote"
        defaultSizes={{
          width: { xs: "96%", sm: "92%", md: 880 },
          height: { xs: "92%", sm: "88%", md: "82%" },
        }}
        preventParentTrigger
      >
        <QuoteView quoteId={quote.id} />
      </BaseModalComponent> */}
      <Stack spacing={3}>
        {/* Header Card */}
        <Card sx={cardStyles}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: statusChipProps(invoiceStatus).bgcolor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReceiptIcon sx={{ fontSize: 28, color: statusChipProps(invoiceStatus).color }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: colors.gray[700] }}>
                  {invoiceNumber}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray[400] }}>
                  {invoice?.identifierId ? `Invoice ID: ${invoice?.identifierId}` : `Invoice UID: ${invoice?.uid}`}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  <Chip
                    icon={statusChipProps(invoiceStatus).icon}
                    label={getInvoiceStatusLabel(invoiceStatus)}
                    sx={{
                      bgcolor: statusChipProps(invoiceStatus).bgcolor,
                      color: statusChipProps(invoiceStatus).color,
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      height: 32,
                      px: 1,
                      "& .MuiChip-icon": {
                        color: statusChipProps(invoiceStatus).color,
                      },
                    }}
                  />

                  {invoiceStatus === "PAID" && paymentMethodLabel ? (
                    <Chip
                      icon={<PaymentIcon sx={{ fontSize: 14 }} />}
                      label={paymentMethodLabel}
                      size="small"
                      sx={{
                        height: 27,
                        borderRadius: 1.5,
                        bgcolor: paymentMethodChipStyles.bgcolor,
                        color: paymentMethodChipStyles.color,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        border: "none",

                        "& .MuiChip-icon": {
                          ml: 0.75,
                          color: paymentMethodChipStyles.iconColor,
                        },

                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                    />
                  ) : null}

                  {canMarkAsPaid && (
                    <BaseModalComponent
                      modalKey="mark-invoice-paid-modal"
                      modalLabel="Mark Invoice as Paid"
                      defaultSizes={{ width: "450px", height: "auto" }}
                      allowInsidePreventModal
                      elementTrigger={
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                          disabled={isUpdatingInvoice}
                          sx={{
                            bgcolor: colors.success[600],
                            color: "#fff",
                            fontWeight: 700,
                            textTransform: "none",
                            borderRadius: 2,
                            px: 1.5,
                            "&:hover": {
                              bgcolor: colors.success[700],
                            },
                            "&:disabled": {
                              bgcolor: colors.gray[300],
                            },
                          }}
                        >
                          Mark as Paid
                        </Button>
                      }
                    >
                      <MarkAsPaidModalContent
                        invoice={invoice}
                        refetchInvoice={refetchInvoice}
                        invalidateRelatedQueries={invalidateRelatedQueries}
                        modalKey="mark-invoice-paid-modal"
                      />
                    </BaseModalComponent>
                  )}
                </Box>

                {invoiceStatus === "PAID" && paymentNote ? (
                  <Typography
                    variant="caption"
                    sx={{
                      maxWidth: 320,
                      color: colors.gray[500],
                      fontSize: "0.72rem",
                      lineHeight: 1.4,
                      textAlign: "right",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {paymentNote}
                  </Typography>
                ) : null}
              </Box>

              <InvoiceActionsMenu
                invoiceStatus={invoiceStatus}
                hasPayPalPending={hasPayPalPending}
                isEditable={isEditable}
                canRefund={canRefund}
                canCancel={canCancel}
                isSyncingPayment={isSyncingPayment}
                isResendPending={isResendPending}
                isResendCoolingDown={isResendCoolingDown}
                resendRemainingSec={resendRemainingSec}
                isRefundPending={isRefundPending}
                isCancelPending={isCancelPending}
                invoiceUid={invoiceUid}
                handleVerifyPaymentStatus={handleVerifyPaymentStatus}
                handleResendClick={handleResendClick}
                handleRefundClick={handleRefundClick}
                handleCancelClick={handleCancelClick}
              />
            </Box>
          </Box>

          {DialogComponent}

          {isDepositInvoice && quote && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${alphaVar(colors.orange[300], 0.6)}`,
                bgcolor: alphaVar(colors.orange[50], 0.4),
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <ReceiptIcon sx={{ fontSize: 20, color: colors.orange[700] }} />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.orange[700], fontWeight: 700, textTransform: "uppercase", fontSize: "0.65rem", display: "block" }}
                  >
                    Deposit for Quote #{quote.id}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                    Capped at quote grand total of{" "}
                    <strong>${quoteGrandTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </Typography>
                </Box>
              </Box>
              {/* {quote?.id != null && (

                <BaseModalComponent
                  modalKey={`quote-deposit-${quote?.id}`}
                  modalLabel="Quote details"
                  preventParentTrigger={true}
                  defaultSizes={{
                    width: { xs: "96%", sm: "92%", md: 880 },
                    height: { xs: "92%", sm: "88%", md: "82%" },
                  }}
                  elementTrigger={
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LinkIcon sx={{ fontSize: 18 }} />}
                      // onClick={() => {
                      //   const modalKey = `QuoteRow-ViewQuote-${quote?.id}`;
                      //   openModal(modalKey, <QuoteView quoteId={quote.id} />, {
                      //     preventParentTrigger: true,
                      //     defaultSizes: {
                      //       width: { xs: "96%", sm: "92%", md: 880 },
                      //       height: { xs: "92%", sm: "88%", md: "82%" },
                      //     },

                      //     modalLabel: "Quote",
                      //   });
                      // }}
                      sx={{ borderColor: colors.orange[300], color: colors.orange[700], "&:hover": { borderColor: colors.orange.base, bgcolor: alphaVar(colors.orange.base, 0.04) } }}
                    >
                      View Quote
                    </Button>}>
                  <QuoteView quoteId={quote?.id} />
                </BaseModalComponent>


              )} */}
              {quote?.id == null && quote.quoteUri && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LinkIcon sx={{ fontSize: 18 }} />}
                  onClick={() => window.open(quote.quoteUri, "_blank", "noopener,noreferrer")}
                  sx={{
                    borderColor: colors.orange[300],
                    color: colors.orange[700],
                    "&:hover": { borderColor: colors.orange.base, bgcolor: alphaVar(colors.orange.base, 0.04) },
                  }}
                >
                  View PDF
                </Button>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Dates */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ color: colors.gray[400], fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>
                Created de
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 14, color: colors.gray[500] }} />
                <Typography variant="body2" fontWeight={600} sx={{ color: colors.gray[700] }}>
                  {formatDateTime(invoice?.createdAt)}
                </Typography>
              </Box>
            </Box>
            {invoice?.updatedAt && (
              <Box>
                <Typography variant="caption" sx={{ color: colors.gray[400], fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>
                  Last Updated
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: 14, color: colors.gray[500] }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: colors.gray[700] }}>
                    {formatDateTime(invoice?.updatedAt)}
                  </Typography>
                </Box>
              </Box>
            )}
            {isPaid && invoice?.paidAt && (
              <Box>
                <Typography variant="caption" sx={{ color: colors.gray[400], fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}>
                  Paid At
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 14, color: colors.success[700] }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: colors.success[700] }}>
                    {formatDateTime(invoice?.paidAt)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Card>

        {/* Client Information */}
        {client && (
          <Card sx={cardStyles}>
            <Typography variant="h6" fontWeight={700} sx={{ color: colors.gray[700], mb: 2 }}>
              Client Information
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
                <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                  <strong>Name:</strong> {clientName}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
                <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                  <strong>Email:</strong> {clientEmail}
                </Typography>
              </Box>
              {clientPhone !== "N/A" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Phone sx={{ fontSize: 18, color: colors.gray[500] }} />
                  <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                    <strong>Phone:</strong> {clientPhone}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Card>
        )}

        {relatedDepositInvoiceUid ? (
          <InvoiceRelatedInvoicesCard
            depositInvoiceUid={relatedDepositInvoiceUid}
            scopeOfWorkUid={scopeOfWorkUid ?? invoice?.scopeOfWorkUid ?? undefined}
            depositAmount={relatedDepositAmountLabel ?? undefined}
          />
        ) : null}

        {billedJobEntries.length > 0 && (
          <Card sx={cardStyles}>
            <Typography variant="h6" fontWeight={700} sx={{ color: colors.gray[700], mb: 2 }}>
              Service Line Items
            </Typography>
            <Stack spacing={2} divider={<Divider />}>
              {billedJobEntries.map(([jobId, events]) => {
                const job = events[0]?.job;

                const jobAlias = job?.alias?.trim?.() || "";

                const serviceNameOverride = scopeOfWork?.serviceNameOverride?.trim() || job?.scopeOfWork?.serviceNameOverride?.trim() || "";

                const originalServiceName = scopeOfWork?.typeOfService?.name?.trim() || job?.scopeOfWork?.typeOfService?.name?.trim() || "";
                const serviceName = serviceNameOverride || originalServiceName || `Job #${jobId}`;
                const jobDescription = job?.description?.trim() || null;

                const serviceDescription = !serviceNameOverride
                  ? job?.scopeOfWork?.typeOfService?.description?.trim() || scopeOfWork?.typeOfService?.description?.trim() || null
                  : null;
                return (
                  <Box
                    key={jobId}
                    sx={{
                      py: 1,
                    }}
                  >
                    <Box
                      sx={{
                        mb: 1.75,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: colors.gray[50],
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.gray[800],
                          fontWeight: 800,
                          lineHeight: 1.35,
                        }}
                      >
                        Job #{jobId} · {serviceName}
                      </Typography>

                      {jobAlias ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.gray[500],
                            display: "block",
                            mt: 0.35,
                            fontWeight: 600,
                          }}
                        >
                          Job alias: {jobAlias}
                        </Typography>
                      ) : null}

                      {serviceDescription || jobDescription ? (
                        <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                          {serviceDescription ? (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: colors.gray[400],
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  display: "block",
                                  mb: 0.35,
                                }}
                              >
                                Service description
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: colors.gray[600],
                                  fontSize: "0.82rem",
                                  lineHeight: 1.5,
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {serviceDescription}
                              </Typography>
                            </Box>
                          ) : null}

                          {jobDescription ? (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: colors.gray[400],
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  display: "block",
                                  mb: 0.35,
                                }}
                              >
                                Job description
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: colors.gray[600],
                                  fontSize: "0.82rem",
                                  lineHeight: 1.5,
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {jobDescription}
                              </Typography>
                            </Box>
                          ) : null}
                        </Stack>
                      ) : null}
                    </Box>

                    <Stack spacing={1} divider={<Divider />}>
                      {events.map((ev, idx) => {
                        let visitAmount = 0;
                        const savedLines = (invoice as any)?.pricingDetail?.serviceLines;

                        if (Array.isArray(savedLines)) {
                          const matchedLine = savedLines.find((line) => line && line.eventUid === ev.uid);

                          if (matchedLine && typeof matchedLine.lineTotal === "number") {
                            visitAmount = matchedLine.lineTotal;
                          } else {
                            const jobLines = savedLines.filter((line) => line && line.jobId === jobId);

                            if (jobLines.length > 0) {
                              const jobTotal = jobLines.reduce((sum, line) => sum + (line.lineTotal ?? 0), 0);

                              visitAmount = jobTotal / events.length;
                            } else {
                              const jobTotal = serviceLineAmountByJob.get(jobId) ?? 0;

                              visitAmount = jobTotal / events.length;
                            }
                          }
                        } else {
                          const jobTotal = serviceLineAmountByJob.get(jobId) ?? 0;

                          visitAmount = jobTotal / events.length;
                        }

                        const startValid = Boolean(ev.startDate && !Number.isNaN(new Date(ev.startDate).getTime()));

                        const dateTitle = startValid
                          ? new Intl.DateTimeFormat("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "2-digit",
                            }).format(new Date(ev.startDate))
                          : `Visit ${idx + 1}`;

                        const tz = job?.company?.timeZone ?? "UTC";

                        const timeSubtitle =
                          startValid && ev.endDate && !Number.isNaN(new Date(ev.endDate).getTime())
                            ? formatBillingVisitWindow(ev.startDate, ev.endDate, tz)
                            : "";

                        const visitExtras = getExtraServicesForVisit(persistedExtraLineItems, ev.uid, billedEvents.length);
                        const visitExtrasTotal = visitExtras.reduce((sum, item) => sum + item.amount, 0);
                        const totalVisitAmount = visitAmount + visitExtrasTotal;

                        return (
                          <Box
                            key={ev.uid}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              px: 1.5,
                              py: 1,
                              gap: 2,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: colors.gray[700],
                                  fontWeight: 700,
                                }}
                              >
                                Visit {idx + 1} — {dateTitle}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  color: colors.gray[500],
                                  display: "block",
                                  mt: 0.2,
                                  textTransform: "capitalize",
                                }}
                              >
                                {timeSubtitle ? `${timeSubtitle} · ` : ""}
                                {ev.status?.replace(/_/g, " ")}
                              </Typography>

                              {visitExtras.length > 0 && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.75 }}>
                                  {visitExtras.map((extra, eIdx) => (
                                    <Chip
                                      key={`${extra.id ?? extra.title}-${eIdx}`}
                                      label={`+ ${extra.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${extra.title}`}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: "0.72rem",
                                        fontWeight: 600,
                                        bgcolor: alphaVar(colors.orange[50] ?? "#FFF7ED", 1),
                                        color: colors.orange[800] ?? "#9A3412",
                                        border: `1px solid ${alphaVar(colors.orange[200] ?? "#FED7AA", 1)}`,
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </Box>

                            <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: colors.gray[800],
                                  fontWeight: 800,
                                }}
                              >
                                $
                                {totalVisitAmount.toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </Typography>
                              {visitExtrasTotal > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: colors.gray[500],
                                    display: "block",
                                    fontSize: "0.68rem",
                                  }}
                                >
                                  Base: ${visitAmount.toFixed(2)} + Extra: ${visitExtrasTotal.toFixed(2)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
            {billedJobEntries.length > 1 && !invoice?.pricingDetail?.serviceLines && serviceSubtotalValue > 0 && (
              <Typography variant="caption" sx={{ color: colors.gray[400], display: "block", mt: 1.5 }}>
                Service subtotal is distributed across billed jobs.
              </Typography>
            )}
          </Card>
        )}

        {/* Cost Breakdown */}
        <Card sx={cardStyles}>
          <Typography variant="h6" fontWeight={700} sx={{ color: colors.gray[700], mb: 2 }}>
            Cost Breakdown
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: 600, color: colors.gray[500], fontSize: "0.813rem", borderBottom: `1px solid ${colors.gray[200]}`, py: 1.5 }}
                  >
                    Item
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: colors.gray[500], fontSize: "0.813rem", borderBottom: `1px solid ${colors.gray[200]}`, py: 1.5 }}
                  >
                    Quantity
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: colors.gray[500], fontSize: "0.813rem", borderBottom: `1px solid ${colors.gray[200]}`, py: 1.5 }}
                  >
                    Unit Price
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: colors.gray[500], fontSize: "0.813rem", borderBottom: `1px solid ${colors.gray[200]}`, py: 1.5 }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Main Areas Cost */}
                {hasMainAreasData && (
                  <>
                    <TableRow sx={{ "&:hover": { bgcolor: alphaVar(colors.gray[50], 0.5) } }}>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("mainAreas") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {mainAreas.length > 0 ? (
                            <IconButton size="small" onClick={() => toggleRow("mainAreas")}>
                              {isRowExpanded("mainAreas") ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 34 }} />
                          )}
                          Main Areas Cost
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: colors.gray[600], borderBottom: isRowExpanded("mainAreas") ? "none" : `1px solid ${colors.gray[100]}`, py: 1.5 }}
                      >
                        {invoice?.mainAreasCost?.quantity?.value || 0} {invoice?.mainAreasCost?.quantity?.unit || ""}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: colors.gray[600], borderBottom: isRowExpanded("mainAreas") ? "none" : `1px solid ${colors.gray[100]}`, py: 1.5 }}
                      >
                        {formatAmount(invoice?.mainAreasCost?.price)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("mainAreas") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {formatAmount(invoiceCosts.mainCost)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0, borderBottom: `1px solid ${colors.gray[100]}` }} colSpan={4}>
                        <Collapse in={isRowExpanded("mainAreas")} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 6, bgcolor: alphaVar(colors.gray[50], 0.3) }}>
                            {mainAreas.length > 0 ? (
                              mainAreas.map((area, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    py: 1,
                                    borderBottom: index < mainAreas.length - 1 ? `1px solid ${colors.gray[100]}` : "none",
                                  }}
                                >
                                  <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                                    {area.area?.name || `Area ${index + 1}`}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: colors.gray[700], fontWeight: 500 }}>
                                    {/* Add your area cost formatting here */}
                                    {/* {area.areaUid} */}
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" sx={{ color: colors.gray[500], textAlign: "center" }}>
                                No main areas.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                )}

                {/* Other Areas Cost */}
                {hasOtherAreasData && (
                  <>
                    <TableRow sx={{ "&:hover": { bgcolor: alphaVar(colors.gray[50], 0.5) } }}>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("otherAreas") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {otherAreas.length > 0 ? (
                            <IconButton size="small" onClick={() => toggleRow("otherAreas")}>
                              {isRowExpanded("otherAreas") ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 34 }} />
                          )}
                          Other Areas Cost
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("otherAreas") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {invoice?.otherAreasCost?.quantity?.value || 0} {invoice?.otherAreasCost?.quantity?.unit || ""}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("otherAreas") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {formatAmount(invoice?.otherAreasCost?.price)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("otherAreas") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {formatAmount(invoiceCosts.otherCost)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0, borderBottom: `1px solid ${colors.gray[100]}` }} colSpan={4}>
                        <Collapse in={isRowExpanded("otherAreas")} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 6, bgcolor: alphaVar(colors.gray[50], 0.3) }}>
                            {otherAreas.length > 0 ? (
                              otherAreas.map((area, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    py: 1,
                                    borderBottom: index < otherAreas.length - 1 ? `1px solid ${colors.gray[100]}` : "none",
                                  }}
                                >
                                  <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                                    {area.area?.name || `Area ${index + 1}`}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: colors.gray[700], fontWeight: 500 }}>
                                    {/* Add your area cost formatting here */}
                                    {/* {area.areaUid} */}
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" sx={{ color: colors.gray[500], textAlign: "center" }}>
                                No other areas.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                )}

                {/* Additional Objects Cost */}
                {hasAdditionalObjectsData && (
                  <>
                    <TableRow sx={{ "&:hover": { bgcolor: alphaVar(colors.gray[50], 0.5) } }}>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("additionalObjects") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {additionalObjects.length > 0 ? (
                            <IconButton size="small" onClick={() => toggleRow("additionalObjects")}>
                              {isRowExpanded("additionalObjects") ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 34 }} />
                          )}
                          Additional Objects Cost
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("additionalObjects") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {invoice?.additionalObjectsCost?.quantity?.value || 0} {invoice?.additionalObjectsCost?.quantity?.unit || ""}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("additionalObjects") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {formatAmount(invoice?.additionalObjectsCost?.price)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("additionalObjects") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {formatAmount(invoiceCosts.additionalCost)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0, borderBottom: `1px solid ${colors.gray[100]}` }} colSpan={4}>
                        <Collapse in={isRowExpanded("additionalObjects")} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 6, bgcolor: alphaVar(colors.gray[50], 0.3) }}>
                            {additionalObjects.length > 0 ? (
                              additionalObjects.map((object, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    py: 1,
                                    borderBottom: index < additionalObjects.length - 1 ? `1px solid ${colors.gray[100]}` : "none",
                                  }}
                                >
                                  <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                                    {object.name || `Area ${index + 1}`}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: colors.gray[700], fontWeight: 500 }}>
                                    {/* Add your area cost formatting here */}
                                    {/* {object.uid} */}
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" sx={{ color: colors.gray[500], textAlign: "center" }}>
                                No additional Objects
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                )}

                {/* Special Requests Cost */}
                {hasSpecialRequestsData && (
                  <>
                    <TableRow sx={{ "&:hover": { bgcolor: alphaVar(colors.gray[50], 0.5) } }}>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("specialRequests") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {specialRequests.length > 0 ? (
                            <IconButton size="small" onClick={() => toggleRow("specialRequests")}>
                              {isRowExpanded("specialRequests") ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 34 }} />
                          )}
                          Special Requests Cost
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("specialRequests") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {invoice?.specialRequestsCost?.quantity?.value || 0} {invoice?.specialRequestsCost?.quantity?.unit || ""}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("specialRequests") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {formatAmount(invoice?.specialRequestsCost?.price)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("specialRequests") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {formatAmount(invoiceCosts.specialCost)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0, borderBottom: `1px solid ${colors.gray[100]}` }} colSpan={4}>
                        <Collapse in={isRowExpanded("specialRequests")} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 6, bgcolor: alphaVar(colors.gray[50], 0.3) }}>
                            {specialRequests.map((object, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  py: 1,
                                  borderBottom: index < specialRequests.length - 1 ? `1px solid ${colors.gray[100]}` : "none",
                                }}
                              >
                                <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                                  {object.name || `Area ${index + 1}`}
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.gray[700], fontWeight: 500 }}>
                                  {/* Add your area cost formatting here */}
                                  {/* {object.uid} */}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                )}

                {/* Deposit Amount */}
                {quote?.depositAmount != null && quote.depositAmount > 0 && (
                  <TableRow sx={{ "&:hover": { bgcolor: alphaVar(colors.gray[50], 0.5) } }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: colors.gray[700],
                        borderBottom: `1px solid ${colors.gray[100]}`,
                        py: 1.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 5 }}>Deposit Amount</Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: colors.gray[600],
                        borderBottom: `1px solid ${colors.gray[100]}`,
                        py: 1.5,
                      }}
                    >
                      1
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: colors.gray[600],
                        borderBottom: `1px solid ${colors.gray[100]}`,
                        py: 1.5,
                      }}
                    >
                      ${quote.depositAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        color: colors.gray[700],
                        borderBottom: `1px solid ${colors.gray[100]}`,
                        py: 1.5,
                      }}
                    >
                      ${quote.depositAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                )}

                {persistedExtraLineItems.length > 0 && (
                  <>
                    <TableRow sx={{ "&:hover": { bgcolor: alphaVar(colors.gray[50], 0.5) } }}>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("extraLineItems") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <IconButton size="small" onClick={() => toggleRow("extraLineItems")}>
                            {isRowExpanded("extraLineItems") ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                          </IconButton>
                          Extra line items
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("extraLineItems") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {persistedExtraLineItems.length} items
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("extraLineItems") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        -
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("extraLineItems") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        ${persistedExtraLineItemsTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0, borderBottom: `1px solid ${colors.gray[100]}` }} colSpan={4}>
                        <Collapse in={isRowExpanded("extraLineItems")} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 6, bgcolor: alphaVar(colors.gray[50], 0.3) }}>
                            {persistedExtraLineItems.map((item, index) => {
                              const targetInfo = getVisitTargetsForExtraItem(item, billedEvents);
                              return (
                                <Box
                                  key={`${item.title}-${index}`}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    py: 1,
                                    borderBottom: index < persistedExtraLineItems.length - 1 ? `1px solid ${colors.gray[100]}` : "none",
                                  }}
                                >
                                  <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                      <Typography variant="body2" sx={{ color: colors.gray[700], fontWeight: 600 }}>
                                        {item.title}
                                      </Typography>
                                      {targetInfo.summaryLabel ? (
                                        <Chip
                                          label={targetInfo.summaryLabel}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: "0.68rem",
                                            fontWeight: 600,
                                            bgcolor: targetInfo.isAllVisits
                                              ? alphaVar(colors.blue[50] ?? "#EFF6FF", 1)
                                              : alphaVar(colors.gray[100], 0.8),
                                            color: targetInfo.isAllVisits ? (colors.blue[700] ?? "#1D4ED8") : colors.gray[700],
                                            border: `1px solid ${
                                              targetInfo.isAllVisits ? alphaVar(colors.blue[200] ?? "#BFDBFE", 1) : colors.gray[200]
                                            }`,
                                          }}
                                        />
                                      ) : null}
                                    </Box>
                                    {item.description ? (
                                      <Typography variant="caption" sx={{ color: colors.gray[500], display: "block" }}>
                                        {item.description}
                                      </Typography>
                                    ) : null}
                                    {targetInfo.targets.length > 1 && !targetInfo.isAllVisits ? (
                                      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.25 }}>
                                        {targetInfo.targets.map((tgt) => (
                                          <Typography
                                            key={tgt.eventUid}
                                            variant="caption"
                                            sx={{
                                              color: colors.gray[600],
                                              bgcolor: alphaVar(colors.gray[100], 0.6),
                                              px: 0.75,
                                              py: 0.2,
                                              borderRadius: 0.75,
                                              fontSize: "0.68rem",
                                            }}
                                          >
                                            {tgt.label}: $$
                                            {tgt.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </Typography>
                                        ))}
                                      </Box>
                                    ) : null}
                                  </Box>
                                  <Typography variant="body2" sx={{ color: colors.gray[700], fontWeight: 600, whiteSpace: "nowrap" }}>
                                    ${item.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                )}

                {/* Service fees */}
                {persistedServiceFees.length > 0 ? (
                  <>
                    <TableRow sx={{ "&:hover": { bgcolor: alphaVar(colors.gray[50], 0.5) } }}>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("serviceFees") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <IconButton size="small" onClick={() => toggleRow("serviceFees")}>
                            {isRowExpanded("serviceFees") ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                          </IconButton>
                          {persistedServiceFeeLabel || "Service fees"}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("serviceFees") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {persistedServiceFees.length} {persistedServiceFees.length === 1 ? "item" : "items"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("serviceFees") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        -
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("serviceFees") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {formatUsdAmount(persistedServiceFeesTotal)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0, borderBottom: `1px solid ${colors.gray[100]}` }} colSpan={4}>
                        <Collapse in={isRowExpanded("serviceFees")} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 6, bgcolor: alphaVar(colors.gray[50], 0.3) }}>
                            {persistedServiceFees.map((item, index) => (
                              <Box
                                key={`${item.label}-${index}`}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 2,
                                  py: 1,
                                  borderBottom: index < persistedServiceFees.length - 1 ? `1px solid ${colors.gray[100]}` : "none",
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                                    {item.label}
                                  </Typography>
                                  {item.description ? (
                                    <Typography variant="caption" sx={{ color: colors.gray[500], display: "block", mt: 0.25 }}>
                                      {item.description}
                                    </Typography>
                                  ) : null}
                                </Box>
                                <Typography variant="body2" sx={{ color: colors.gray[700], fontWeight: 600, whiteSpace: "nowrap" }}>
                                  {formatUsdAmount(item.amount)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow sx={{ "&:hover": { bgcolor: alphaVar(colors.gray[50], 0.5) } }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: colors.gray[700],
                        borderBottom: `1px solid ${colors.gray[100]}`,
                        py: 1.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 5 }}>{persistedServiceFeeLabel || "Service Fee"}</Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: colors.gray[600],
                        borderBottom: `1px solid ${colors.gray[100]}`,
                        py: 1.5,
                      }}
                    >
                      {invoice?.increasePercentageByFrequency?.quantity?.value || 0} {invoice?.increasePercentageByFrequency?.quantity?.unit || ""}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: colors.gray[600],
                        borderBottom: `1px solid ${colors.gray[100]}`,
                        py: 1.5,
                      }}
                    >
                      {formatAmount(invoice?.increasePercentageByFrequency?.price)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        color: colors.gray[700],
                        borderBottom: `1px solid ${colors.gray[100]}`,
                        py: 1.5,
                      }}
                    >
                      {formatAmount(invoice?.increasePercentageByFrequency)}
                    </TableCell>
                  </TableRow>
                )}

                {persistedDiscounts.length > 0 && (
                  <>
                    <TableRow sx={{ "&:hover": { bgcolor: alphaVar(colors.gray[50], 0.5) } }}>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("discounts") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <IconButton size="small" onClick={() => toggleRow("discounts")}>
                            {isRowExpanded("discounts") ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                          </IconButton>
                          Discounts
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("discounts") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {persistedDiscounts.length} {persistedDiscounts.length === 1 ? "item" : "items"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: colors.gray[600],
                          borderBottom: isRowExpanded("discounts") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        -
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: colors.gray[700],
                          borderBottom: isRowExpanded("discounts") ? "none" : `1px solid ${colors.gray[100]}`,
                          py: 1.5,
                        }}
                      >
                        {formatUsdAmount(persistedDiscountsTotal, true)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0, borderBottom: `1px solid ${colors.gray[100]}` }} colSpan={4}>
                        <Collapse in={isRowExpanded("discounts")} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 6, bgcolor: alphaVar(colors.gray[50], 0.3) }}>
                            {persistedDiscounts.map((item, index) => (
                              <Box
                                key={`${item.description}-${index}`}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 2,
                                  py: 1,
                                  borderBottom: index < persistedDiscounts.length - 1 ? `1px solid ${colors.gray[100]}` : "none",
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                                    {item.description}
                                  </Typography>
                                  {item.detail ? (
                                    <Typography variant="caption" sx={{ color: colors.gray[500], display: "block", mt: 0.25 }}>
                                      {item.detail}
                                    </Typography>
                                  ) : null}
                                </Box>
                                <Typography variant="body2" sx={{ color: colors.gray[700], fontWeight: 600, whiteSpace: "nowrap" }}>
                                  {formatUsdAmount(item.amount, true)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                )}

                {/* Total Cost Row - not collapsible */}
                <TableRow sx={{ bgcolor: alphaVar(colors.orange[50], 0.2) }}>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.938rem",
                      color: colors.gray[800],
                      borderTop: `2px solid ${colors.orange[300]}`,
                      borderBottom: "none",
                      py: 2,
                    }}
                  >
                    Total Cost
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.938rem",
                      color: colors.gray[700],
                      borderTop: `2px solid ${colors.orange[300]}`,
                      borderBottom: "none",
                      py: 2,
                    }}
                  >
                    {/* {invoice?.totalCost?.quantity?.value || 0} {invoice?.totalCost?.quantity?.unit || ""} */}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.938rem",
                      color: colors.gray[700],
                      borderTop: `2px solid ${colors.orange[300]}`,
                      borderBottom: "none",
                      py: 2,
                    }}
                  >
                    {/* {formatAmount(invoice?.totalCost?.price)} */}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.938rem",
                      color: colors.orange.base,
                      borderTop: `2px solid ${colors.orange[300]}`,
                      borderBottom: "none",
                      py: 2,
                    }}
                  >
                    {isEditingTotal ? (
                      <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                          <TextField
                            size="small"
                            value={totalDraft}
                            error={exceedsQuoteTotal}
                            onChange={(e) => setTotalDraft(e.target.value.replace(/[^\d.]/g, ""))}
                            inputProps={{ inputMode: "decimal", style: { textAlign: "right", width: 100 } }}
                          />
                          <IconButton size="small" color="primary" onClick={saveTotal} disabled={isUpdatingInvoice || exceedsQuoteTotal}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEditingTotal} disabled={isUpdatingInvoice}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {isDepositInvoice && quoteGrandTotal > 0 && (
                          <Typography variant="caption" sx={{ color: exceedsQuoteTotal ? colors.danger[600] : colors.gray[400], fontWeight: 500 }}>
                            {exceedsQuoteTotal
                              ? `Cannot exceed quote total of $${quoteGrandTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : `Max $${quoteGrandTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (quote total)`}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        {formatAmount(invoice?.totalCost)}
                        {isEditable && (
                          <IconButton size="small" onClick={startEditingTotal} sx={{ ml: 0.5 }}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
                {persistedTipAmount > 0 ? (
                  <TableRow sx={{ "&:hover": { bgcolor: alphaVar(colors.gray[50], 0.5) } }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: colors.gray[700],
                        borderBottom: `1px solid ${colors.gray[100]}`,
                        py: 1.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 5 }}>
                        Tip{persistedTipDetail ? ` (${persistedTipDetail})` : ""}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: `1px solid ${colors.gray[100]}`, py: 1.5 }} />
                    <TableCell align="right" sx={{ borderBottom: `1px solid ${colors.gray[100]}`, py: 1.5 }} />
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        color: colors.gray[700],
                        borderBottom: `1px solid ${colors.gray[100]}`,
                        py: 1.5,
                      }}
                    >
                      {formatUsdAmount(persistedTipAmount)}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {(persistedServiceFeeLabel || persistedExtraLineItems.length > 0) && (
          <Card sx={cardStyles}>
            <Typography variant="h6" fontWeight={700} sx={{ color: colors.gray[700], mb: 2 }}>
              Create Invoice Snapshot
            </Typography>
            {persistedServiceFeeLabel && (
              <Typography variant="body2" sx={{ color: colors.gray[600], mb: 1 }}>
                <strong>Service fee label:</strong> {persistedServiceFeeLabel}
              </Typography>
            )}
            {persistedExtraLineItems.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.gray[700], mb: 1 }}>
                  Extra line items
                </Typography>
                <Stack spacing={0.75}>
                  {persistedExtraLineItems.map((item, idx) => (
                    <Box key={`${item.title}-${idx}`} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: colors.gray[600] }}>
                          {item.title}
                        </Typography>
                        {item.description ? (
                          <Typography variant="caption" sx={{ color: colors.gray[500], display: "block", mt: 0.25 }}>
                            {item.description}
                          </Typography>
                        ) : null}
                      </Box>
                      <Typography variant="body2" sx={{ color: colors.gray[700], fontWeight: 600, whiteSpace: "nowrap" }}>
                        ${item.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Card>
        )}

        {/* Payment Methods */}
        {(isEditable || (invoice?.paymentMethods && invoice.paymentMethods.length > 0)) && (
          <PaymentMethodsEditor
            paymentMethods={invoice?.paymentMethods || []}
            isEditable={isEditable}
            isSaving={isUpdatingInvoice}
            onSave={handleSavePaymentMethods}
            getPaymentMethodLabel={getPaymentMethodLabel}
            isOnlinePayment={isOnlinePayment}
            isBankTransfer={isBankTransfer}
            isBalanceFullyCoveredByDeposit={isBalanceFullyCoveredByDeposit(invoice)}
            cardStyles={cardStyles}
            availablePayments={paymentAvailableData}
          />
        )}

        {/* Payment History */}
        {isEditable && invoice?.uid && (
          <InvoiceSavedCardCharge
            invoiceUid={invoice.uid}
            onCharged={() => {
              invalidateRelatedQueries();
              refetchInvoice?.();
            }}
          />
        )}

        {invoice?.allowPartialPayments && invoice?.payments && invoice.payments.length > 0 && (
          <Card sx={cardStyles}>
            <Typography variant="h6" fontWeight={700} sx={{ color: colors.gray[700], mb: 2 }}>
              Payment History
            </Typography>
            <Stack spacing={2} divider={<Divider />}>
              {invoice.payments.map((p, idx) => {
                const isCompleted = p.status?.toLowerCase() === "completed";
                const isFailed = p.status?.toLowerCase() === "failed";
                const statusColor = isCompleted ? colors.success[700] : isFailed ? colors.danger[700] : colors.warning[700];
                const statusBg = isCompleted ? colors.success[100] : isFailed ? colors.danger[100] : colors.warning[100];

                return (
                  <Box key={p.uid || idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ color: colors.gray[700] }}>
                          Payment #{idx + 1}
                        </Typography>
                        <Chip
                          label={p.status}
                          size="small"
                          sx={{
                            bgcolor: statusBg,
                            color: statusColor,
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: colors.gray[400], display: "block", mt: 0.5 }}>
                        {p?.paymentMethod ? `Provider: ${getPaymentMethodLabel(p?.paymentMethod as EPaymentMethod)}` : "Provider: Unknown"}
                        {p?.paidAt ? ` · Paid: ${formatDateTime(p?.paidAt)}` : p?.createdAt ? ` · Created: ${formatDateTime(p?.createdAt)}` : ""}
                        {p?.transactionId ? ` · Ref: ${p?.transactionId}` : ""}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ color: colors.gray[800] }}>
                      {typeof p.amount === "number"
                        ? `$${p.amount.toFixed(2)}`
                        : isNaN(Number(p.amount))
                          ? p.amount
                          : `$${Number(p.amount).toFixed(2)}`}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Card>
        )}

        {/* Email Recipients */}
        {invoice?.emailRecipients && invoice?.emailRecipients.length > 0 && (
          <Card sx={cardStyles}>
            <EmailRecipientsEditor
              recipients={invoice.emailRecipients}
              isEditable={isEditable}
              isSaving={isUpdatingRecipients}
              needsCanPay={(invoice.paymentMethods || []).some(
                (pm: { provider?: string }) =>
                  pm.provider === EPaymentMethod.PAYPAL || pm.provider === EPaymentMethod.STRIPE || pm.provider === EPaymentMethod.BANK_TRANSFER,
              )}
              onSave={handleSaveRecipients}
            />
          </Card>
        )}

        {/* Notes */}
        {invoice?.notes && (
          <Card sx={cardStyles}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <NotesIcon sx={{ fontSize: 20, color: colors.gray[500] }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: colors.gray[700] }}>
                Notes
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: colors.gray[600], whiteSpace: "pre-wrap" }}>
              {invoice?.notes}
            </Typography>
          </Card>
        )}

        {/* Billed Job Events Editor */}
        <InvoiceEventsEditor
          invoice={invoice}
          clientUid={client?.uid}
          isEditable={isEditable}
          isSaving={isUpdatingInvoice}
          onSave={handleSaveJobEvents}
          cardStyles={cardStyles}
        />
      </Stack>
    </Box>
  );
};

interface MarkAsPaidModalContentProps {
  invoice: any;
  refetchInvoice?: () => void;
  invalidateRelatedQueries: () => void;
  modalKey: string;
}

const MarkAsPaidModalContent = ({ invoice, refetchInvoice, invalidateRelatedQueries, modalKey }: MarkAsPaidModalContentProps) => {
  const { closeByKey } = useModals();
  const { showSnackbar } = useGlobalSnackbar();
  const mutateMarkAsPaid = useSetInvoiceAsPaid();
  const [method, setMethod] = useState<"bank_transfer" | "cash">("bank_transfer");
  const [transactionId, setTransactionId] = useState("");

  const handleConfirm = async () => {
    try {
      await mutateMarkAsPaid.mutateAsync({
        invoice,
        companyId: Number(localStorage.getItem("companyId")),
        method,
        transactionId: transactionId.trim() || null,
      });
      invalidateRelatedQueries();
      refetchInvoice?.();
      showSnackbar("Invoice marked as paid successfully", "success");
      closeByKey(modalKey);
    } catch (error) {
      showSnackbar("Failed to mark invoice as paid", "error");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" sx={{ mb: 2, color: colors.gray[700] }}>
        Choose how this invoice was paid:
      </Typography>

      <FormControl component="fieldset" sx={{ width: "100%", mb: 3 }}>
        <RadioGroup value={method} onChange={(e) => setMethod(e.target.value as "bank_transfer" | "cash")}>
          <FormControlLabel
            value="bank_transfer"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: colors.gray[800] }}>
                  Bank Transfer
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray[500] }}>
                  Wire, ACH, or online bank transfer
                </Typography>
              </Box>
            }
            sx={{ mb: 1.5 }}
          />
          <FormControlLabel
            value="cash"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: colors.gray[800] }}>
                  Cash / Cashier
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray[500] }}>
                  Physical cash, check, or local payment
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      {method === "bank_transfer" && (
        <TextField
          fullWidth
          size="small"
          label="Transaction ID / Reference (Optional)"
          value={transactionId}
          onChange={(e) => setTransactionId(e.target.value)}
          sx={{ mb: 3 }}
          placeholder="e.g. ACH reference or bank folio"
        />
      )}

      {method === "cash" && (
        <TextField
          fullWidth
          size="small"
          label="Receipt Reference / Note (Optional)"
          value={transactionId}
          onChange={(e) => setTransactionId(e.target.value)}
          sx={{ mb: 3 }}
          placeholder="e.g. cash receipt number or notes"
        />
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button onClick={() => closeByKey(modalKey)} disabled={mutateMarkAsPaid.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={mutateMarkAsPaid.isPending}
          sx={{ bgcolor: colors.success[600], "&:hover": { bgcolor: colors.success[700] } }}
        >
          Mark as Paid
        </Button>
      </Stack>
    </Box>
  );
};
